import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ChevronDown,
  ChevronRight,
  FileText,
  LayoutGrid,
  List,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { TopBar } from '../components/TopBar'
import { useI18n } from '../lib/i18n'
import type { Matter } from '../lib/types'
import { CASE_TYPES, COURT_LOCATIONS, translateCaseType, translateCourtLocation } from '../lib/caseConstants'

// ─── i18n ────────────────────────────────────────────────────────────────────
const LABELS = {
  zh: {
    pageTitle: '案件列表',
    searchPlaceholder: '搜索案件名称、编号或律所名称...',
    filterAll: '全部',
    filterActive: '进行中',
    filterClosed: '已完成',
    newCase: '新建案件',
    newCaseDesc: '上传材料，开始AI分析',
    statusPlanning: '规划中',
    statusActive: '进行中',
    statusClosed: '已完成',
    deleteCase: '删除案件',
    deleteConfirm: '确定要删除此案件吗？此操作不可恢复。',
    filterPlanning: '规划中',
    practiceLabel: '律师识别案：',
    noMatters: '暂无案件',
    // 新建案件弹窗
    modalTitle: '新建案件',
    modalSubtitle: '填写案件基本信息并上传相关材料',
    caseName: '案件名称',
    caseNamePlaceholder: '请输入案件名称',
    caseNumber: '案件编号',
    caseNumberPlaceholder: '如：MAS-2024-005',
    caseType: '案件类型',
    caseTypeSelect: '选择类型',
    caseSummary: '案件简介',
    caseSummaryPlaceholder: '请简要描述案件背景、主要争议及诉求...',
    linkedKb: '挂接知识库：',
    linkedKbHint: '选择与案件相关的知识库分类，AI 将结合这些知识进行分析',
    uploadMaterials: '上传案件材料',
    uploadHint: '点击或拖拽文件到此处上传',
    uploadFormats: '支持 PDF、Word、图片等格式',
    cancel: '取消',
    createCase: '创建案件',
    required: '*',
    // 案件类型选项
    typeDivorce: '离婚案',
    typeContract: '合同纠纷案',
    typeProperty: '财产分割案',
    typeDebt: '债务追偿案',
    typeEmployment: '雇佣纠纷案',
    typeOther: '其他',
    customType: '自定义类型',
    customTypePlaceholder: '请输入案件类型',
    courtLocation: '法院所在地',
    courtLocationPlaceholder: '选择法院所在地',
    customLocation: '自定义所在地',
    customLocationPlaceholder: '请输入法院所在地',
    jurisdiction: '法院级别/类型',
    jurisdictionPlaceholder: '如：联邦法院、高等法庭、地方法庭等',
    courtName: '法院名称',
    courtNamePlaceholder: '请输入法院名称',
  },
  en: {
    pageTitle: 'Cases',
    searchPlaceholder: 'Search by name, number or Law Firm Name...',
    filterAll: 'All',
    filterActive: 'Active',
    filterClosed: 'Closed',
    newCase: 'New Case',
    newCaseDesc: 'Upload materials, start AI analysis',
    statusPlanning: 'Planning',
    statusActive: 'Active',
    statusClosed: 'Closed',
    deleteCase: 'Delete Case',
    deleteConfirm: 'Are you sure you want to delete this case? This action cannot be undone.',
    filterPlanning: 'Planning',
    practiceLabel: 'Practice area: ',
    noMatters: 'No cases yet',
    modalTitle: 'New Case',
    modalSubtitle: 'Fill in basic case information and upload related materials',
    caseName: 'Case Name',
    caseNamePlaceholder: 'Enter case name',
    caseNumber: 'Case Number',
    caseNumberPlaceholder: 'e.g. MAS-2024-005',
    caseType: 'Case Type',
    caseTypeSelect: 'Select type',
    caseSummary: 'Case Summary',
    caseSummaryPlaceholder: 'Briefly describe the case background, disputes and claims...',
    linkedKb: 'Link Knowledge Base:',
    linkedKbHint: 'Select relevant knowledge base categories for AI analysis',
    uploadMaterials: 'Upload Case Materials',
    uploadHint: 'Click or drag files here to upload',
    uploadFormats: 'Supports PDF, Word, images, etc.',
    cancel: 'Cancel',
    createCase: 'Create Case',
    required: '*',
    typeDivorce: 'Divorce',
    typeContract: 'Contract Dispute',
    typeProperty: 'Property Division',
    typeDebt: 'Debt Recovery',
    typeEmployment: 'Employment Dispute',
    typeOther: 'Other',
    customType: 'Custom Type',
    customTypePlaceholder: 'Enter case type',
    courtLocation: 'Court Location',
    courtLocationPlaceholder: 'Select court location',
    customLocation: 'Custom Location',
    customLocationPlaceholder: 'Enter court location',
    jurisdiction: 'Court Level/Type',
    jurisdictionPlaceholder: 'e.g., Federal Court, High Court, Magistrates Court',
    courtName: 'Court Name',
    courtNamePlaceholder: 'Enter court name',
  },
}

type FilterTab = 'all' | 'planning' | 'active' | 'closed'
type ViewMode = 'grid' | 'list'

export function MatterList() {
  const { lang } = useI18n()
  const t = LABELS[lang] ?? LABELS.zh
  const navigate = useNavigate()

  const [matters, setMatters] = useState<Matter[]>([])
  const [filter, setFilter] = useState<FilterTab>('all')
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [searchTerm, setSearchTerm] = useState('')
  const [showNewCase, setShowNewCase] = useState(false)

  // 根据筛选条件请求后端
  useEffect(() => {
    const params = new URLSearchParams({ limit: '50' })
    if (filter !== 'all') {
      params.append('status', filter)
    }
    if (searchTerm.trim()) {
      params.append('q', searchTerm.trim())
    }

    fetch(`/matters?${params.toString()}`)
      .then((r) => r.json())
      .then((data: { items?: Matter[] }) => {
        if (data.items) setMatters(data.items)
      })
      .catch(() => {})
  }, [filter, searchTerm])

  return (
    <>
      <TopBar title={t.pageTitle} />
      <div className="flex-1 overflow-y-auto p-6">
        {/* 搜索栏 + 筛选 + 新建按钮 */}
        <div className="flex items-center gap-4 mb-6">
          {/* 搜索框 */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t.searchPlaceholder}
              className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-brand-200 text-sm text-brand-700 placeholder:text-brand-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
            />
          </div>

          {/* 筛选 tabs */}
          <div className="flex items-center bg-brand-100 rounded-lg p-0.5">
            {(['all', 'planning', 'active', 'closed'] as FilterTab[]).map((tab) => {
              const label =
                tab === 'all'
                  ? t.filterAll
                  : tab === 'planning'
                    ? t.filterPlanning
                    : tab === 'active'
                      ? t.filterActive
                      : t.filterClosed
              const isActive = filter === tab
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setFilter(tab)}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-brand-600 hover:text-brand-800'
                  }`}
                >
                  {label}
                </button>
              )
            })}
          </div>

          {/* 视图切换 */}
          <div className="flex items-center bg-brand-100 rounded-lg p-0.5">
            {(['grid', 'list'] as ViewMode[]).map((mode) => {
              const isActive = viewMode === mode
              const Icon = mode === 'grid' ? LayoutGrid : List
              const label = mode === 'grid'
                ? lang === 'zh' ? '卡片视图' : 'Card view'
                : lang === 'zh' ? '列表视图' : 'List view'
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setViewMode(mode)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-brand-500 hover:text-brand-800'
                  }`}
                  title={label}
                  aria-label={label}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden xl:inline">{label}</span>
                </button>
              )
            })}
          </div>

          {/* 新建按钮 */}
          <button
            type="button"
            onClick={() => setShowNewCase(true)}
            className="ml-auto inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t.newCase}
          </button>
        </div>

        {/* 案件列表 */}
        {matters.length === 0 && !searchTerm ? (
          <div className="text-center text-brand-400 text-sm py-16">
            {t.noMatters}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {matters.map((matter) => (
              <Link key={matter.matter_id} to={`/matters/${matter.matter_id}`} className="block">
                <MatterCard
                  matter={matter}
                  t={t}
                  lang={lang}
                  onDelete={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (!window.confirm(t.deleteConfirm)) return
                    fetch(`/matters/${matter.matter_id}`, { method: 'DELETE' })
                      .then((r) => {
                        if (r.ok) setMatters((prev) => prev.filter((m) => m.matter_id !== matter.matter_id))
                      })
                      .catch(() => {})
                  }}
                />
              </Link>
            ))}
            <NewCaseCard t={t} onClick={() => setShowNewCase(true)} />
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-brand-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-brand-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-brand-900">
                {lang === 'zh' ? '案件列表' : 'Case list'}
              </h3>
              <button
                type="button"
                onClick={() => setShowNewCase(true)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                {t.newCase}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="divide-y divide-brand-100">
              {matters.map((matter) => (
                <MatterListRow
                  key={matter.matter_id}
                  matter={matter}
                  t={t}
                  lang={lang}
                  onDelete={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (!window.confirm(t.deleteConfirm)) return
                    fetch(`/matters/${matter.matter_id}`, { method: 'DELETE' })
                      .then((r) => {
                        if (r.ok) setMatters((prev) => prev.filter((m) => m.matter_id !== matter.matter_id))
                      })
                      .catch(() => {})
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 新建案件弹窗 */}
      {showNewCase && (
        <NewCaseModal
          t={t}
          onClose={() => setShowNewCase(false)}
          onCreated={(m) => {
            setMatters((prev) => [m, ...prev])
            setShowNewCase(false)
            navigate(`/matters/${m.matter_id}`)
          }}
        />
      )}
    </>
  )
}

// ─── 案件卡片 ────────────────────────────────────────────────────────────────
function MatterCard({
  matter,
  t,
  lang,
  onDelete,
}: {
  matter: Matter
  t: (typeof LABELS)['zh']
  lang: 'zh' | 'en'
  onDelete: (e: React.MouseEvent) => void
}) {
  const statusLabel =
    matter.status === 'planning'
      ? t.statusPlanning
      : matter.status === 'active'
        ? t.statusActive
        : t.statusClosed
  const statusColor =
    matter.status === 'planning'
      ? 'bg-amber-100 text-amber-700'
      : matter.status === 'active'
        ? 'bg-blue-100 text-blue-700'
        : 'bg-green-100 text-green-700'

  // 从 practice_area 生成标签
  const tags: string[] = []
  if (matter.practice_area) tags.push(translateCaseType(matter.practice_area, lang))

  return (
    <div className="bg-white rounded-xl border border-brand-200 p-5 hover:border-brand-300 hover:shadow-sm transition-all group cursor-pointer relative">
      {/* 删除按钮 */}
      <button
        type="button"
        onClick={onDelete}
        className="absolute top-3 right-3 p-1.5 rounded-lg text-brand-300 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-500 transition-all"
        title={t.deleteCase}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>

      {/* 头部：图标 + 标题 + 状态 */}
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
          <FileText className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-brand-800 truncate">
              {matter.title}
            </h4>
            <span
              className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${statusColor}`}
            >
              {statusLabel}
            </span>
          </div>
          <div className="text-xs text-brand-400 font-mono mt-0.5">
            {matter.case_number}
          </div>
        </div>
      </div>

      {/* 描述 */}
      {matter.summary && (
        <p className="text-xs text-brand-500 leading-relaxed mb-3 line-clamp-2">
          {matter.summary}
        </p>
      )}

      {/* 案件详情信息 */}
      <div className="space-y-1.5 mb-3 text-xs">
        {matter.court && (
          <div className="flex items-start gap-2">
            <span className="text-brand-400 min-w-[60px]">{lang === 'zh' ? '法院：' : 'Court:'}</span>
            <span className="text-brand-600 flex-1">{matter.court}</span>
          </div>
        )}
        {matter.court_location && (
          <div className="flex items-start gap-2">
            <span className="text-brand-400 min-w-[60px]">{lang === 'zh' ? '所在地：' : 'Location:'}</span>
            <span className="text-brand-600 flex-1">{translateCourtLocation(matter.court_location, lang)}</span>
          </div>
        )}
        {matter.jurisdiction && (
          <div className="flex items-start gap-2">
            <span className="text-brand-400 min-w-[60px]">{lang === 'zh' ? '职权：' : 'Jurisdiction:'}</span>
            <span className="text-brand-600 flex-1">{matter.jurisdiction}</span>
          </div>
        )}
        {matter.client_name && (
          <div className="flex items-start gap-2">
            <span className="text-brand-400 min-w-[60px]">{lang === 'zh' ? '客户：' : 'Client:'}</span>
            <span className="text-brand-600 flex-1">{matter.client_name}</span>
          </div>
        )}
        {matter.opposing_party && (
          <div className="flex items-start gap-2">
            <span className="text-brand-400 min-w-[60px]">{lang === 'zh' ? '对方：' : 'Opposing:'}</span>
            <span className="text-brand-600 flex-1">{matter.opposing_party}</span>
          </div>
        )}
      </div>

      {/* 标签 */}
      {tags.length > 0 && (
        <div className="flex items-center gap-1.5 mb-3">
          <span className="text-[11px] text-brand-400">{t.practiceLabel}</span>
          {tags.map((tag) => (
            <span
              key={tag}
              className="px-2 py-0.5 rounded bg-brand-100 text-[11px] text-brand-600 font-medium"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* 底部：日期 + 箭头 */}
      <div className="flex items-center justify-between pt-2 border-t border-brand-100">
        <span className="text-xs text-brand-400 flex items-center gap-1">
          📅 {matter.opened_at}
        </span>
        <ChevronRight className="w-4 h-4 text-brand-300 group-hover:text-brand-500 transition-colors" />
      </div>
    </div>
  )
}

function MatterListRow({
  matter,
  t,
  lang,
  onDelete,
}: {
  matter: Matter
  t: (typeof LABELS)['zh']
  lang: 'zh' | 'en'
  onDelete: (e: React.MouseEvent) => void
}) {
  const statusLabel =
    matter.status === 'planning'
      ? t.statusPlanning
      : matter.status === 'active'
        ? t.statusActive
        : t.statusClosed
  const statusColor =
    matter.status === 'planning'
      ? 'bg-amber-100 text-amber-700'
      : matter.status === 'active'
        ? 'bg-blue-100 text-blue-700'
        : 'bg-green-100 text-green-700'

  return (
    <Link
      to={`/matters/${matter.matter_id}`}
      className="group flex items-center gap-4 px-5 py-4 hover:bg-brand-50 transition-colors"
    >
      <div className="w-9 h-9 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors">
        <FileText className="w-4 h-4 text-brand-500 group-hover:text-blue-600 transition-colors" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <h4 className="text-sm font-semibold text-brand-900 truncate">
            {matter.title}
          </h4>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${statusColor}`}>
            {statusLabel}
          </span>
        </div>
        {matter.summary ? (
          <p className="mt-1 text-xs text-brand-500 leading-relaxed truncate">
            {matter.summary}
          </p>
        ) : (
          <p className="mt-1 text-xs text-brand-400">
            {translateCaseType(matter.practice_area, lang) || '—'}
          </p>
        )}
        {/* 案件详细信息 */}
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-brand-400">
          {matter.court && <span>{lang === 'zh' ? '法院' : 'Court'}: {matter.court}</span>}
          {matter.court_location && <span>{lang === 'zh' ? '所在地' : 'Location'}: {translateCourtLocation(matter.court_location, lang)}</span>}
          {matter.jurisdiction && <span>{lang === 'zh' ? '职权' : 'Jurisdiction'}: {matter.jurisdiction}</span>}
          {matter.client_name && <span>{lang === 'zh' ? '客户' : 'Client'}: {matter.client_name}</span>}
          {matter.opposing_party && <span>{lang === 'zh' ? '对方' : 'Opposing'}: {matter.opposing_party}</span>}
        </div>
      </div>

      <div className="hidden md:block w-36 text-right flex-shrink-0">
        <div className="text-xs font-mono text-brand-600 truncate">
          {matter.case_number || '—'}
        </div>
        <div className="mt-1 text-xs text-brand-400">
          {matter.opened_at}
        </div>
      </div>

      <button
        type="button"
        onClick={onDelete}
        className="p-1.5 rounded-lg text-brand-300 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-500 transition-all"
        title={t.deleteCase}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>

      <ChevronRight className="w-4 h-4 text-brand-300 group-hover:text-brand-500 transition-colors" />
    </Link>
  )
}

// ─── 新建案件卡片 ────────────────────────────────────────────────────────────
function NewCaseCard({ t, onClick }: { t: (typeof LABELS)['zh']; onClick: () => void }) {
  return (
    <div onClick={onClick} className="rounded-xl border-2 border-dashed border-brand-300 p-5 flex flex-col items-center justify-center min-h-[200px] hover:border-blue-400 hover:bg-blue-50/50 transition-colors cursor-pointer group">
      <div className="w-12 h-12 rounded-full bg-brand-100 flex items-center justify-center mb-3 group-hover:bg-blue-100 transition-colors">
        <Plus className="w-6 h-6 text-brand-400 group-hover:text-blue-600 transition-colors" />
      </div>
      <div className="text-sm font-medium text-brand-700 group-hover:text-blue-700">
        {t.newCase}
      </div>
      <div className="text-xs text-brand-400 mt-1">{t.newCaseDesc}</div>
    </div>
  )
}

// ─── 新建案件弹窗 ────────────────────────────────────────────────────────────
const KB_TAGS = [
  { key: 'divorce', zh: '离婚案', en: 'Divorce' },
  { key: 'contract', zh: '合同纠纷案', en: 'Contract Dispute' },
  { key: 'property', zh: '财产分割案', en: 'Property Division' },
  { key: 'debt', zh: '债务追偿案', en: 'Debt Recovery' },
  { key: 'employment', zh: '雇佣纠纷案', en: 'Employment Dispute' },
]

function NewCaseModal({
  t,
  onClose,
  onCreated,
}: {
  t: (typeof LABELS)['zh']
  onClose: () => void
  onCreated: (matter: Matter) => void
}) {
  const { lang } = useI18n()
  const [title, setTitle] = useState('')
  const [caseNumber, setCaseNumber] = useState('')
  const [practiceArea, setPracticeArea] = useState('')
  const [customPracticeArea, setCustomPracticeArea] = useState('')
  const [court, setCourt] = useState('')
  const [courtLocation, setCourtLocation] = useState('')
  const [customCourtLocation, setCustomCourtLocation] = useState('')
  const [jurisdiction, setJurisdiction] = useState('')
  const [summary, setSummary] = useState('')
  const [selectedKb, setSelectedKb] = useState<string[]>([])
  const [files, setFiles] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const toggleKb = useCallback((key: string) => {
    setSelectedKb((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    )
  }, [])

  const handleFiles = useCallback((newFiles: FileList | null) => {
    if (!newFiles) return
    setFiles((prev) => [...prev, ...Array.from(newFiles)])
  }, [])

  const handleSubmit = async () => {
    if (!title.trim() || !summary.trim()) return
    setSubmitting(true)
    try {
      // 确定最终的 practice_area
      const finalPracticeArea = practiceArea === 'other' 
        ? customPracticeArea.trim() 
        : practiceArea

      // 确定最终的 court_location
      const finalCourtLocation = courtLocation === 'other'
        ? customCourtLocation.trim()
        : courtLocation

      // 1. 创建案件
      const res = await fetch('/matters', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          summary: summary.trim(),
          practice_area: finalPracticeArea || null,
          court: court.trim() || null,
          court_location: finalCourtLocation || null,
          jurisdiction: jurisdiction || null,
          case_number: caseNumber.trim() || null,
          metadata: {
            linked_kb: selectedKb,
          },
        }),
      })
      if (!res.ok) throw new Error('create failed')
      const matter: Matter = await res.json()

      // 2. 上传案件材料（与案件详情页共用同一接口）
      if (files.length > 0) {
        for (const file of files) {
          const formData = new FormData()
          formData.append('file', file)
          await fetch(`/matters/${matter.matter_id}/files?category=other`, {
            method: 'POST',
            body: formData,
          })
        }
      }

      onCreated(matter)
    } catch {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 遮罩 */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* 弹窗 */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto mx-4">
        <div className="p-6">
          {/* 标题 */}
          <div className="flex items-start justify-between mb-1">
            <div>
              <h2 className="text-xl font-bold text-brand-800">{t.modalTitle}</h2>
              <p className="text-sm text-brand-400 mt-0.5">{t.modalSubtitle}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-5 mt-6">
            {/* 案件名称 */}
            <div>
              <label className="block text-sm font-medium text-brand-800 mb-1.5">
                {t.caseName} <span className="text-red-500">{t.required}</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t.caseNamePlaceholder}
                className="w-full px-4 py-2.5 rounded-lg border border-brand-200 text-sm text-brand-700 placeholder:text-brand-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
              />
            </div>

            {/* 案件编号 + 案件类型 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-brand-800 mb-1.5">
                  {t.caseNumber}
                </label>
                <input
                  type="text"
                  value={caseNumber}
                  onChange={(e) => setCaseNumber(e.target.value)}
                  placeholder={t.caseNumberPlaceholder}
                  className="w-full px-4 py-2.5 rounded-lg border border-brand-200 text-sm text-brand-700 placeholder:text-brand-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-800 mb-1.5">
                  {t.caseType}
                </label>
                <div className="relative">
                  <select
                    value={practiceArea}
                    onChange={(e) => setPracticeArea(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-brand-200 text-sm text-brand-700 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 appearance-none bg-white pr-10"
                  >
                    <option value="">{t.caseTypeSelect}</option>
                    {CASE_TYPES.map((ct) => (
                      <option key={ct.value} value={ct.value}>
                        {lang === 'zh' ? ct.zh : ct.en}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* 自定义案件类型（当选择"其他"时显示） */}
            {practiceArea === 'other' && (
              <div>
                <label className="block text-sm font-medium text-brand-800 mb-1.5">
                  {t.customType}
                </label>
                <input
                  type="text"
                  value={customPracticeArea}
                  onChange={(e) => setCustomPracticeArea(e.target.value)}
                  placeholder={t.customTypePlaceholder}
                  className="w-full px-4 py-2.5 rounded-lg border border-brand-200 text-sm text-brand-700 placeholder:text-brand-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                />
              </div>
            )}

            {/* 法院名称 + 法院所在地 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-brand-800 mb-1.5">
                  {t.courtName}
                </label>
                <input
                  type="text"
                  value={court}
                  onChange={(e) => setCourt(e.target.value)}
                  placeholder={t.courtNamePlaceholder}
                  className="w-full px-4 py-2.5 rounded-lg border border-brand-200 text-sm text-brand-700 placeholder:text-brand-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-800 mb-1.5">
                  {t.courtLocation}
                </label>
                <div className="relative">
                  <select
                    value={courtLocation}
                    onChange={(e) => setCourtLocation(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-brand-200 text-sm text-brand-700 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 appearance-none bg-white pr-10"
                  >
                    <option value="">{t.courtLocationPlaceholder}</option>
                    {COURT_LOCATIONS.map((loc) => (
                      <option key={loc.value} value={loc.value}>
                        {lang === 'zh' ? loc.zh : loc.en}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* 自定义法院所在地（当选择"其他"时显示） */}
            {courtLocation === 'other' && (
              <div>
                <label className="block text-sm font-medium text-brand-800 mb-1.5">
                  {t.customLocation}
                </label>
                <input
                  type="text"
                  value={customCourtLocation}
                  onChange={(e) => setCustomCourtLocation(e.target.value)}
                  placeholder={t.customLocationPlaceholder}
                  className="w-full px-4 py-2.5 rounded-lg border border-brand-200 text-sm text-brand-700 placeholder:text-brand-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                />
              </div>
            )}

            {/* 法院级别/类型 */}
            <div>
              <label className="block text-sm font-medium text-brand-800 mb-1.5">
                {t.jurisdiction}
              </label>
              <input
                type="text"
                value={jurisdiction}
                onChange={(e) => setJurisdiction(e.target.value)}
                placeholder={t.jurisdictionPlaceholder}
                className="w-full px-4 py-2.5 rounded-lg border border-brand-200 text-sm text-brand-700 placeholder:text-brand-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
              />
            </div>

            {/* 案件简介 */}
            <div>
              <label className="block text-sm font-medium text-brand-800 mb-1.5">
                {t.caseSummary} <span className="text-red-500">{t.required}</span>
              </label>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder={t.caseSummaryPlaceholder}
                rows={4}
                className="w-full px-4 py-2.5 rounded-lg border border-brand-200 text-sm text-brand-700 placeholder:text-brand-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 resize-none"
              />
            </div>

            {/* 挂接知识库 */}
            <div>
              <label className="block text-sm font-medium text-brand-800 mb-2">
                {t.linkedKb}
              </label>
              <div className="flex flex-wrap gap-2 mb-1.5">
                {KB_TAGS.map((kb) => {
                  const selected = selectedKb.includes(kb.key)
                  return (
                    <button
                      key={kb.key}
                      type="button"
                      onClick={() => toggleKb(kb.key)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        selected
                          ? 'bg-blue-50 border-blue-300 text-blue-700'
                          : 'bg-white border-brand-200 text-brand-600 hover:border-brand-300'
                      }`}
                    >
                      {lang === 'zh' ? kb.zh : kb.en}
                    </button>
                  )
                })}
              </div>
              <p className="text-xs text-blue-500">{t.linkedKbHint}</p>
            </div>

            {/* 上传案件材料 */}
            <div>
              <label className="block text-sm font-medium text-brand-800 mb-1.5">
                {t.uploadMaterials}
              </label>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation() }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleFiles(e.dataTransfer.files) }}
                className="border-2 border-dashed border-brand-300 rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors"
              >
                <FileText className="w-10 h-10 text-brand-300 mb-2" />
                <p className="text-sm text-brand-500">{t.uploadHint}</p>
                <p className="text-xs text-brand-400 mt-1">{t.uploadFormats}</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />
              {/* 已选文件列表 */}
              {files.length > 0 && (
                <div className="mt-2 space-y-1">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-brand-50 rounded-lg text-xs text-brand-600">
                      <FileText className="w-3.5 h-3.5 text-brand-400" />
                      <span className="flex-1 truncate">{f.name}</span>
                      <button
                        type="button"
                        onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                        className="text-brand-400 hover:text-red-500"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 底部按钮 */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-brand-100">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-lg border border-brand-300 text-sm font-medium text-brand-600 hover:bg-brand-50 transition-colors"
            >
              {t.cancel}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!title.trim() || !summary.trim() || submitting}
              className="px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t.createCase}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
